import React, { useState, useEffect, useCallback } from 'react'
import { Plus, Users } from 'lucide-react'
import { Button, Input, SearchInput } from '../ui'
import { WorkerCard } from './WorkerCard'
import { useThemeTokens } from '../../themes'
import { searchWorkers, createWorker, getToken } from '../../lib/api'
import type { Worker } from '../../types'

interface WorkerPoolProps {
  excludeWorkerIds?: number[]
  onWorkerAdd?: (worker: Worker) => void
  className?: string
  'data-testid'?: string
}

export const WorkerPool: React.FC<WorkerPoolProps> = ({
  excludeWorkerIds = [],
  onWorkerAdd,
  className,
  'data-testid': testId
}) => {
  const theme = useThemeTokens()
  const [workers, setWorkers] = useState<Worker[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAddWorker, setShowAddWorker] = useState(false)
  const [newWorkerName, setNewWorkerName] = useState('')
  const [isAddingWorker, setIsAddingWorker] = useState(false)

  const loadWorkers = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const token = getToken()
      if (!token) {
        throw new Error('Authentication required')
      }
      
      const response = await searchWorkers(token, searchQuery)
      setWorkers(response.workers)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workers')
    } finally {
      setLoading(false)
    }
  }, [searchQuery])

  // Load workers on mount and when search changes
  useEffect(() => {
    loadWorkers()
  }, [loadWorkers])

  const handleAddWorker = async () => {
    if (!newWorkerName.trim()) return
    
    setIsAddingWorker(true)
    setError(null)
    
    try {
      const token = getToken()
      if (!token) {
        throw new Error('Authentication required')
      }
      
      const createResult = await createWorker(token, { name: newWorkerName.trim() })
      const newWorker = createResult.worker
      setWorkers(prev => [newWorker, ...prev])
      setNewWorkerName('')
      setShowAddWorker(false)
      
      // Optionally add to timesheet immediately
      onWorkerAdd?.(newWorker)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create worker')
    } finally {
      setIsAddingWorker(false)
    }
  }

  // Filter out workers that are already on the timesheet
  const availableWorkers = workers.filter(worker => 
    !excludeWorkerIds.includes(worker.id)
  )

  // Container styles
  const containerStyles: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`,
    padding: theme.spacing.lg,
    boxShadow: theme.shadows.sm,
    height: '600px',
    display: 'flex',
    flexDirection: 'column'
  }

  // Header styles
  const headerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.md
  }



  // Workers list styles
  const workersListStyles: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    marginBottom: theme.spacing.md
  }

  // Add worker form styles
  const addWorkerFormStyles: React.CSSProperties = {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.sm,
    border: `1px solid ${theme.colors.border}`,
    marginBottom: theme.spacing.sm
  }

  // Empty state styles
  const emptyStateStyles: React.CSSProperties = {
    textAlign: 'center',
    padding: theme.spacing.xl,
    color: theme.colors.textSecondary
  }

  return (
    <div style={containerStyles} className={className} data-testid={testId}>
      {/* Header */}
      <div style={headerStyles}>
        <h3 style={{
          fontSize: theme.typography.fontSize.lg,
          fontWeight: theme.typography.fontWeight.bold,
          color: theme.colors.text,
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.sm
        }}>
          <Users size={20} />
          Worker Pool
        </h3>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowAddWorker(!showAddWorker)}
        >
          <Plus size={16} />
          Add Worker
        </Button>
      </div>

      {/* Add worker form */}
      {showAddWorker && (
        <div style={addWorkerFormStyles}>
          <div style={{ marginBottom: theme.spacing.sm }}>
            <Input
              placeholder="Enter worker name"
              value={newWorkerName}
              onChange={(e) => setNewWorkerName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddWorker()}
              disabled={isAddingWorker}
            />
          </div>
          <div style={{ display: 'flex', gap: theme.spacing.sm }}>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddWorker}
              disabled={isAddingWorker || !newWorkerName.trim()}
            >
              {isAddingWorker ? 'Adding...' : 'Add'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowAddWorker(false)
                setNewWorkerName('')
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: theme.spacing.md }}>
        <SearchInput
          placeholder="Search workers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Error display */}
      {error && (
        <div style={{
          padding: theme.spacing.sm,
          backgroundColor: theme.colors.error + '15',
          border: `1px solid ${theme.colors.error}`,
          borderRadius: theme.borderRadius.sm,
          color: theme.colors.error,
          fontSize: theme.typography.fontSize.sm,
          marginBottom: theme.spacing.md
        }}>
          {error}
        </div>
      )}

      {/* Workers list */}
      <div style={workersListStyles}>
        {loading ? (
          <div style={emptyStateStyles}>
            Loading workers...
          </div>
        ) : availableWorkers.length === 0 ? (
          <div style={emptyStateStyles}>
            {searchQuery ? 'No workers found' : 'No workers available'}
          </div>
        ) : (
          availableWorkers.map(worker => (
            <WorkerCard
              key={worker.id}
              worker={worker}
              draggable={true}
            />
          ))
        )}
      </div>

      {/* Instructions */}
      <div style={{
        padding: theme.spacing.sm,
        backgroundColor: theme.colors.background,
        borderRadius: theme.borderRadius.sm,
        fontSize: theme.typography.fontSize.sm,
        color: theme.colors.textSecondary,
        textAlign: 'center'
      }}>
        Drag workers to today's timesheet
      </div>
    </div>
  )
} 